import React, { useCallback, useState } from 'react'
import { Search } from 'lucide-react'
import CustomTable from '@/shared/components/ui/Button/CustomTable'
import AccountDetailsDrawer from './AccountDetailsDrawer'
import AddToAccountsButton from './AddToAccountsButton'
import ResultsCount from '@/shared/components/ui/Status/ResultsCount'
import { renderResultCell } from './ResultCells'
import { RESULT_COLUMNS, SKELETON_ROWS, TABLE_PROPS } from './config'
import { getAccountKey } from './utils'

const CustomTableComponent = CustomTable as React.ComponentType<any>

interface DiscoveryResultsTableProps {
    accounts: any[]
    isLoading?: boolean
    isError?: boolean
    loadingText?: string
    emptyTitle: string
    emptyDescription: string
    /** Actions cell; defaults to "add to accounts". */
    renderAction?: (account: any) => React.ReactNode
    /** Drawer chip text for the score. */
    scoreLabel?: (score: number) => string
}

const Message = ({ title, description }: { title: string; description?: string }) => (
    <div className='rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-12 flex flex-col items-center justify-center text-center shadow-xs'>
        <div className='w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-400 mb-3'>
            <Search size={22} />
        </div>
        <h3 className='text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1'>{title}</h3>
        {description && <p className='text-xs text-gray-500 dark:text-gray-400 max-w-sm'>{description}</p>}
    </div>
)

/** The custom-search results layout (count, table, details drawer) for any list of accounts. */
export default function DiscoveryResultsTable({
    accounts,
    isLoading = false,
    isError = false,
    loadingText,
    emptyTitle,
    emptyDescription,
    renderAction,
    scoreLabel,
}: DiscoveryResultsTableProps) {
    const [selectedAccount, setSelectedAccount] = useState<any>(null)

    // Clicking the same account again closes the panel.
    const toggleAccount = useCallback((account: any) => {
        setSelectedAccount((current: any) =>
            current && getAccountKey(current) === getAccountKey(account) ? null : account,
        )
    }, [])

    const renderCell = useCallback(
        (account: any, columnKey: string) => renderResultCell(account, columnKey, toggleAccount, renderAction),
        [toggleAccount, renderAction],
    )

    let body: React.ReactNode
    if (isError && !isLoading) {
        body = (
            <Message
                title='We could not load these accounts right now'
                description='Please try again in a moment.'
            />
        )
    } else if (!isLoading && accounts.length === 0) {
        body = <Message title={emptyTitle} description={emptyDescription} />
    } else {
        body = (
            <div className='flex-1 min-h-0 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden shadow-xs'>
                <CustomTableComponent
                    data={isLoading ? SKELETON_ROWS : accounts}
                    columns={RESULT_COLUMNS}
                    renderCell={renderCell}
                    enableSelection={!isLoading}
                    {...TABLE_PROPS}
                    height='100%'
                />
            </div>
        )
    }

    return (
        <>
            <div className='flex flex-col gap-4 w-full h-full px-6 pb-4'>
                <ResultsCount isLoading={isLoading} count={accounts.length} loadingText={loadingText} />
                {body}
            </div>
            <AccountDetailsDrawer
                account={selectedAccount}
                onClose={() => setSelectedAccount(null)}
                scoreLabel={scoreLabel}
                renderFooter={renderAction ? undefined : (account) => <AddToAccountsButton account={account} />}
            />
        </>
    )
}
